import { useCallback, useState } from 'react';
import { useDataProvider } from 'react-admin';

export interface DetachUnit {
  id: number;
  serial?: string | null;
  hostname?: string | null;
}

export interface DetachResult {
  succeeded: Array<{ id: number; serial: string | null; hostname: string | null }>;
  failed: Array<{ id: number; serial: string | null; hostname: string | null; error: string }>;
  successCount: number;
  failureCount: number;
  totalCount: number;
}

export interface UseDetachUnitsFromChannel {
  detach: (units: DetachUnit[], targetChannelId: number) => Promise<DetachResult>;
  loading: boolean;
}

export function useDetachUnitsFromChannel(): UseDetachUnitsFromChannel {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);

  const detach = useCallback(
    async (units: DetachUnit[], targetChannelId: number): Promise<DetachResult> => {
      setLoading(true);

      try {
        const results = await Promise.allSettled(
          units.map((unit) =>
            dataProvider.update('units', {
              id: unit.id,
              data: { update_channel_id: targetChannelId },
              previousData: {},
            }),
          ),
        );

        const succeeded: Array<{ id: number; serial: string | null; hostname: string | null }> = [];
        const failed: Array<{ id: number; serial: string | null; hostname: string | null; error: string }> = [];

        results.forEach((result, index) => {
          const unit = units[index];
          if (result.status === 'fulfilled') {
            succeeded.push({
              id: unit.id,
              serial: unit.serial ?? null,
              hostname: unit.hostname ?? null,
            });
          } else {
            const reason = result.reason as { message?: string; body?: { message?: string } };
            const error = reason?.message ?? reason?.body?.message ?? 'Unknown error';
            failed.push({
              id: unit.id,
              serial: unit.serial ?? null,
              hostname: unit.hostname ?? null,
              error,
            });
          }
        });

        return {
          succeeded,
          failed,
          successCount: succeeded.length,
          failureCount: failed.length,
          totalCount: units.length,
        };
      } finally {
        setLoading(false);
      }
    },
    [dataProvider],
  );

  return { detach, loading };
}
